import React from 'react';
import { 
  Users, 
  TrendingUp, 
  ShieldCheck, 
  Zap, 
  Globe, 
  Award, 
  Clock, 
  BarChart3 
} from 'lucide-react';

// Representing the aggregated stats from your CandidateProfileEntity
const NetworkStats = {
  totalTalent: "4,200+",
  seniorityBreakdown: [
    { label: 'Architect/Principal', value: 28 },
    { label: 'Staff/Lead', value: 42 },
    { label: 'Senior', value: 30 },
  ],
  scaleStats: [
    { label: 'Internet Scale (10M+)', count: '850+' },
    { label: 'Large Scale (1M-10M)', count: '1,200+' },
  ],
  impactHighlights: [
    "Avg. $450k Infrastructure Savings",
    "92% Success in Distributed Systems",
    "85% Interview Ready (1-week notice)"
  ],
  topDomains: ["FinTech", "AI/ML", "E-commerce", "SaaS", "Payments"],
};

const TalentDensityFeature = () => {
  return (
    <div className="bg-slate-50 p-8 rounded-3xl border border-slate-200 shadow-xl max-w-6xl mx-auto font-sans">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 tracking-tight">
            Our Talent Network <span className="text-blue-600">Density</span>
          </h2>
          <p className="text-slate-500 mt-2 text-lg">
            Don't just browse resumes. Access a vetted network of high-impact engineering leaders.
          </p>
        </div>
        <div className="bg-blue-600 text-white px-6 py-3 rounded-2xl shadow-lg shadow-blue-200 flex items-center gap-3">
          <Users size={24} />
          <div className="flex flex-col">
            <span className="text-xs uppercase font-bold opacity-80">Verified Talent</span>
            <span className="text-xl font-bold leading-none">{NetworkStats.totalTalent}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* Section 1: Authority & Seniority */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6 text-blue-600">
            <ShieldCheck size={20} />
            <h3 className="font-bold text-slate-800 uppercase text-sm tracking-wider">Leadership & Authority</h3>
          </div>
          <div className="space-y-4">
            {NetworkStats.seniorityBreakdown.map((item) => (
              <div key={item.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600 font-medium">{item.label}</span>
                  <span className="font-bold text-slate-900">{item.value}%</span>
                </div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div 
                    className="bg-blue-500 h-full rounded-full" 
                    style={{ width: `${item.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-400 italic">
            * 65% of our candidates are "Final Hiring Decision Makers" or "Interview Panelists".
          </p>
        </div>

        {/* Section 2: Technical Scale & Architecture */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6 text-emerald-600">
            <Globe size={20} />
            <h3 className="font-bold text-slate-800 uppercase text-sm tracking-wider">Engineering Scale</h3>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {NetworkStats.scaleStats.map((stat) => (
              <div key={stat.label} className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                <div className="text-2xl font-bold text-emerald-700">{stat.count}</div>
                <div className="text-xs text-emerald-600 font-semibold uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {['Microservices', 'Event-Driven', 'Distributed'].map(tag => (
              <span key={tag} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-1 rounded font-bold">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Section 3: Business Impact & Reputation */}
        <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-inner shadow-black/20">
          <div className="flex items-center gap-3 mb-6 text-amber-400">
            <Award size={20} />
            <h3 className="font-bold uppercase text-sm tracking-wider">Proven Business Impact</h3>
          </div>
          <ul className="space-y-4">
            {NetworkStats.impactHighlights.map((impact, i) => (
              <li key={i} className="flex gap-3 items-start">
                <div className="bg-amber-400/20 p-1 rounded">
                  <Zap size={14} className="text-amber-400" />
                </div>
                <span className="text-sm text-slate-300 leading-snug">{impact}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 pt-6 border-t border-slate-700">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">Avg. Talent Score</span>
              <div className="flex gap-1 text-amber-400">
                {[1,2,3,4,5].map(s => <span key={s}>★</span>)}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Footer Hiring Context */}
      <div className="mt-8 flex flex-col md:flex-row items-center justify-between border-t border-slate-200 pt-8 gap-6">
        <div className="flex items-center gap-8">
           <div className="flex flex-col">
              <span className="text-slate-400 text-xs uppercase font-bold tracking-widest flex items-center gap-2">
                <Clock size={12}/> Hiring Speed
              </span>
              <span className="text-slate-900 font-bold">Decision within 2 weeks</span>
           </div>
           <div className="flex flex-col border-l border-slate-200 pl-8">
              <span className="text-slate-400 text-xs uppercase font-bold tracking-widest flex items-center gap-2">
                <BarChart3 size={12}/> Domain Mastery
              </span>
              <span className="text-slate-900 font-bold">80% FinTech & SaaS Experts</span>
           </div>
        </div>
        <button className="w-full md:w-auto bg-slate-900 hover:bg-black text-white px-8 py-4 rounded-xl font-bold transition-all transform hover:-translate-y-1 active:scale-95 shadow-lg">
          Access Talent Pipeline
        </button>
      </div>
    </div>
  );
};

export default TalentDensityFeature;